import React, { useContext, useEffect, useState } from "react";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import OwTitle from "../../shared/OwTitle/OwTitle";
import { OwFnTable } from "../../shared/OwFnTable/OwFnTable";
import IOwTableCols from "../../../models/IOwTableCols";
import IIntakeForm from "../../../models/IIntakeForm";
import { SocketContext } from "../../../contexts/SocketContext";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IGetIntakeFormsDTO from "../../../models/DTO/IGetIntakeFormsDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetIntakeFormsDTO from "../../../models/DTO/IOnGetIntakeFormsDTO";
import OwButton from "../../shared/OwButton/OwButton";
import { IntakeFormSidebar } from "../IntakeFormSidebar/IntakeFormSidebar";
import UiContext from "../../../contexts/UiContext";
import ReactModal from "react-modal";
import { IntakeFormSetupModal } from "../IntakeFormSetupModal/IntakeFormSetupModal";
import { OwDeleteEditActions } from "../../shared/OwDeleteEditActions/OwDeleteEditActions";
import { ConfirmDeleteOverlayFn } from "../../shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import IOnProcessIntakeFormDTO from "../../../models/DTO/IOnProcessIntakeFormDTO";
import IProcessIntakeFormDTO from "../../../models/DTO/IProcessIntakeFormDTO";

type Props = {};

export const IntakeForms = (props: Props) => {
    const socket = useContext(SocketContext)!;
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);

    const nameOverride = supplierModel.vendor_name_override;

    const breadcrumbs: IBreadcrumbs[] = [
        { title: "vendors".replace("vendor", nameOverride), url: "/vendors".replace("vendor", nameOverride) },
        {
            title: "intake_forms",
            url: "/intake_forms",
        },
    ];

    const renderActionsFn = (row: IIntakeForm, index: number) => {
        return (
            <div>
                <OwDeleteEditActions
                    isEditMode={false}
                    onTrashClickFn={() => {
                        setDeleteElem(`row-${index}`);
                        setDeleteRow(row);
                    }}
                    onEditClickFn={() => {
                        showSidebar(row);
                    }}
                />
            </div>
        );
    };

    const closeModal = () => {
        setModalComponent(null);
        setIsOpen(false);
    };

    const showSetupModal = (row: IIntakeForm) => {
        const component = <IntakeFormSetupModal form={row} closeFn={closeModal} />;

        setModalComponent(component);
        setIsOpen(true);
    };

    const viewSetup = (row: IIntakeForm) => {
        return (
            <div>
                <OwButton
                    title={"setup"}
                    leftIconClass={"fad fa-cogs"}
                    fn={() => {
                        showSetupModal(row);
                    }}
                />
            </div>
        );
    };

    const columns: IOwTableCols[] = [
        {
            isActionsCol: true,
            title: "name",
            valueProp: "name",
            renderFn: renderActionsFn,
            renderFnEmitsRow: true,
            className: "w-5r",
        },
        {
            title: "name",
            valueProp: "name",
            className: "w-15r",
        },
        {
            title: "success_url",
            valueProp: "success_url",
            className: "w-30r",
        },
        {
            title: "",
            valueProp: "",
            renderFn: viewSetup,
            renderFnEmitsRow: true,
        },
    ];

    const [intakeForms, setIntakeForms] = useState<IIntakeForm[]>([]);
    const [isDataFetching, setIsDataFetching] = useState<boolean>(false);
    const [isInitialData, setIsInitialData] = useState<boolean>(true);
    const [modalIsOpen, setIsOpen] = useState<boolean>(false);
    const [modalComponent, setModalComponent] = useState<React.ReactNode | null>(null);
    const [deleteElem, setDeleteElem] = useState<string | null>(null);
    const [deleteRow, setDeleteRow] = useState<IIntakeForm | null>(null);

    const getIntakeForms = () => {
        const payload: IGetIntakeFormsDTO = {
            supplierGuid: supplierModel.guid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            limit: 20,
            offset: 0,
        };

        socket.emit(EndpointsEnum.IntakeFormsGetForms, payload);

        setIsDataFetching(true);
    };

    const onGetIntakeForms = (data: IOnGetIntakeFormsDTO) => {
        setIntakeForms(data.intakeForms);
        setIsDataFetching(false);
        setIsInitialData(false);
    };

    const onProcessIntakeForm = (data: IOnProcessIntakeFormDTO) => {
        getIntakeForms();
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.IntakeFormsOnGetForms, fn: onGetIntakeForms },
        { endpoint: EndpointsEnum.IntakeFormOnProcess, fn: onProcessIntakeForm },
    ];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        getIntakeForms();

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {}, []);

    const closeSidebar = () => {
        uiContext.toggleSidebar(false);
        uiContext.setSidebarComponent(null);

        getIntakeForms();
    };

    const showSidebar = (form?: IIntakeForm) => {
        const component = <IntakeFormSidebar form={form} closeFn={closeSidebar} />;

        uiContext.setSidebarComponent(component);
        uiContext.toggleSidebar(true);
    };

    const confirmDelete = () => {
        if (deleteRow) {
            const payload: IProcessIntakeFormDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                deleteFlag: true,
                form: deleteRow,
            };

            socket.emit(EndpointsEnum.IntakeFormProcess, payload);

            setDeleteRow(null);
            setDeleteElem(null);
        }
    };

    const cancelDelete = () => {
        setDeleteRow(null);
        setDeleteElem(null);
    };

    return (
        <div className={"IntakeForms position-relative"}>
            <Breadcrumbs crumbs={breadcrumbs} />

            <div className="mt-2 d-flex align-items-center">
                <OwTitle title={"intake_forms"} leftIconName={"fad fa-table-rows"} />
                <div>
                    <OwButton
                        className={"ms-2 btn-warning"}
                        title={"create"}
                        leftIconClass={"fad fa-plus"}
                        fn={() => {
                            showSidebar();
                        }}
                    />
                </div>
            </div>

            <div className="mt-2 position-relative">
                <ConfirmDeleteOverlayFn
                    targetElem={deleteElem}
                    handleConfirm={confirmDelete}
                    handleCancel={cancelDelete}
                    nestedParentElem={"intake-forms"}
                    skipMainView={true}
                />

                <OwFnTable
                    id={"intake-forms"}
                    columns={columns}
                    dataSet={intakeForms}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                />
            </div>

            <ReactModal
                isOpen={modalIsOpen}
                shouldCloseOnEsc={true}
                className={"modalClass"}
                overlayClassName={"overlayClass"}
                style={{
                    content: {
                        height: "600px",
                        width: "600px",
                        marginLeft: "auto",
                        marginRight: "auto",
                        marginTop: "auto",
                        marginBottom: "auto",
                    },
                }}>
                {modalComponent}
            </ReactModal>
        </div>
    );
};
